/**
 * AI pieces — an AI (Paste AI Picks, or ChatGPT / Claude through 🔗 AI link) quotes the words it
 * wants from a clip ("keep"), and the ET cuts them. Pure functions, unit-tested.
 *
 * The SAME rules run on the server for exports (emo/mcp_server/pieces.py) — change one, change
 * both, so what the ET shows and what an AI export cuts always agree:
 *
 *  - A phrase matches word for word, ignoring case and punctuation. If the AI dropped or swapped
 *    a middle word, its first and last words anchor the match (when they are close).
 *  - One phrase for a clip trims it (the pen highlight, as before). Two or more phrases for the
 *    same clip — the id repeated, or a list in one "keep" — split it at the phrase edges into the
 *    same pieces a manual ✂ split makes ("123-a", "123-b", …), and each kept piece is ticked
 *    where the AI put it. Pieces may not overlap.
 *  - A piece starts where its first word starts and ends where the next word starts (the clip's
 *    own start/end at its edges) — exactly splitRowAtWord's rule.
 */

const norm = (w) => (w || '').replace(/[‘’]/g, "'").toLowerCase().replace(/[^\p{L}\p{N}']/gu, '');

/**
 * The word-index range {start, end} of an exact phrase inside a clip's sentence, or null.
 * Indices align with sentence.split(' ') and the utterance array (the pen tool's indexing).
 */
export const findPhraseRange = (sentence, keep) => {
  if (!sentence || typeof keep !== 'string' || !keep) return null;
  const normWords = sentence.split(' ').map(norm);
  const keepWords = keep.split(/\s+/).map(norm).filter(Boolean);
  if (!keepWords.length) return null;
  // An exact run of the words (stray empty slots — double spaces, lone dashes — skipped).
  for (let i = 0; i < normWords.length; i++) {
    if (!normWords[i]) continue;
    let k = 0;
    for (let j = i; j < normWords.length; j++) {
      if (!normWords[j]) continue;
      if (normWords[j] !== keepWords[k]) break;
      k++;
      if (k === keepWords.length) return { start: i, end: j };
    }
  }
  // Forgiving fallback: ChatGPT sometimes drops/swaps a middle word so there's no exact run.
  // Anchor by the endpoints instead — first keep-word → last keep-word — as long as that span is
  // a sane length (never grab half the sentence because two anchor words are far apart).
  const start = normWords.indexOf(keepWords[0]);
  if (start === -1) return null;
  let end = -1;
  for (let j = normWords.length - 1; j >= start; j--) { if (normWords[j] === keepWords[keepWords.length - 1]) { end = j; break; } }
  if (end === -1) return null;
  if (normWords.slice(start, end + 1).filter(Boolean).length > keepWords.length + 6) return null;
  return { start, end };
};

const phrasesOf = (keep) => (Array.isArray(keep) ? keep : [keep])
  .filter((k) => typeof k === 'string' && k.trim())
  .map((k) => k.trim());

/**
 * Turn the AI's picks into what plays, in order.
 * @param {Array} items   [{id, keep?}] in play order; keep = a phrase or a list of phrases
 * @param {Function} textOf id → that clip's sentence
 * @returns {{plan: Array<{index, id, phrase, range}>, problems: Array<{id, keep, why}>}}
 *   range null = the whole clip. why = 'not_found' | 'overlaps' | 'duplicate'.
 */
export const planAiPicks = (items, textOf) => {
  const entries = [];
  (items || []).forEach((it, index) => {
    const phrases = phrasesOf(it && it.keep);
    (phrases.length ? phrases : [null]).forEach((phrase) => entries.push({ pos: entries.length, index, id: it.id, phrase }));
  });
  const byId = new Map();
  entries.forEach((e) => {
    const key = String(e.id);
    if (!byId.has(key)) byId.set(key, []);
    byId.get(key).push(e);
  });

  const chosen = [];
  const problems = [];
  byId.forEach((group) => {
    const text = textOf(group[0].id) || '';
    const lastWord = text.split(' ').length - 1;
    const pieces = [];
    const wholes = [];
    group.forEach((e) => {
      if (e.phrase == null) { wholes.push(e); return; }
      const range = findPhraseRange(text, e.phrase);
      if (!range) {
        problems.push({ id: e.id, keep: e.phrase, why: 'not_found' });
        wholes.push(e); // an unmatched phrase falls back to the whole clip
      } else if (range.start <= 0 && range.end >= lastWord) {
        wholes.push(e); // the "phrase" is the whole sentence
      } else if (pieces.some((p) => range.start <= p.range.end && p.range.start <= range.end)) {
        problems.push({ id: e.id, keep: e.phrase, why: 'overlaps' });
      } else {
        pieces.push({ ...e, range });
      }
    });
    if (pieces.length) {
      chosen.push(...pieces);
      wholes.filter((e) => e.phrase == null).forEach((e) => problems.push({ id: e.id, keep: null, why: 'duplicate' }));
    } else {
      chosen.push({ ...wholes[0], range: null });
    }
  });

  chosen.sort((a, b) => a.pos - b.pos);
  return { plan: chosen.map(({ index, id, phrase, range }) => ({ index, id, phrase, range })), problems };
};

const pieceLetter = (k) => (k < 26 ? String.fromCharCode(97 + k) : `p${k}`);

// "HH:MM:SS.mmm" — the row's `duration` pair the Clock shows (same format as createVideo's).
export const toHMS = (totalSeconds) => {
  const ms = Math.round((totalSeconds % 1) * 1000);
  const s = Math.floor(totalSeconds) % 60;
  const m = Math.floor(totalSeconds / 60) % 60;
  const h = Math.floor(totalSeconds / 3600) % 24;
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}.${String(ms).padStart(3, '0')}`;
};

/**
 * Split a clip row at the edges of `ranges` (word ranges to keep, any order) into contiguous
 * pieces — the kept ones and the words between them — shaped exactly like splitRowAtWord's.
 * @returns {{pieces: Array, idFor: (range) => string|undefined}} or null if the row has no
 *   word timings to split by.
 */
export const splitRowIntoPieces = (row, ranges, maxLines) => {
  const words = (row.sentence || '').split(' ');
  const utt = row.utterance || [];
  const last = words.length - 1;
  const cuts = new Set();
  ranges.forEach(({ start, end }) => {
    if (start > 0) cuts.add(start);
    if (end < last) cuts.add(end + 1);
  });
  const edges = [...cuts].sort((a, b) => a - b);
  if (!edges.length || edges[edges.length - 1] >= utt.length) return null;
  const bounds = [0, ...edges, words.length];
  const pieces = [];
  const idByStart = {};
  for (let k = 0; k + 1 < bounds.length; k++) {
    const a = bounds[k];
    const b = bounds[k + 1];
    const start = a === 0 ? row.start_in : utt[a][0];
    const end = b === words.length ? row.end_in : utt[b][0];
    const id = `${row.id}-${pieceLetter(k)}`;
    idByStart[a] = id;
    pieces.push({
      ...row,
      id,
      _splitId: row.id,
      start_in: start,
      end_in: end,
      sentence: words.slice(a, b).join(' '),
      utterance: utt.slice(a, b),
      sec: (end - start).toFixed(2),
      sec_int: Math.floor(end - start),
      sec_float: parseFloat((end - start).toFixed(2)),
      duration_ms: Math.round((end - start) * 1000),
      duration: [toHMS(start), toHMS(end)],
      order: maxLines,
    });
  }
  return { pieces, idFor: (range) => idByStart[range.start] };
};
